import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, X, MapPin, BadgeCheck, ThumbsUp, Truck, Hand, Repeat, Sparkles, RotateCcw, ArrowLeft, HeartHandshake } from "lucide-react";
import { api, imgSrc } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { getCachedCoords, distanceMiles, formatDistance } from "@/lib/geo";

/**
 * Swipe Discover — Tinder-style card stack for marketplace listings.
 *
 * - Pointer-event drag handler (no extra dep)
 * - Pre-fetches the next deck once we get low
 * - Like → records a 'like' swipe + notifies the owner (or surfaces a match if mutual)
 * - Pass → records a 'pass' swipe
 * - Desktop: tap buttons instead of swiping
 */
const DRAG_THRESHOLD = 100; // px

function ListingCardSwipe({ listing, drag, rotation, isTop, dimmed }) {
  const me = getCachedCoords();
  const distMiles = me && typeof listing.lat === "number" && typeof listing.lng === "number"
    ? distanceMiles(me, { lat: listing.lat, lng: listing.lng })
    : null;
  const distText = formatDistance(distMiles);
  const owner = listing.owner || {};
  const wants = (listing.wants || []).slice(0, 4);
  const img = (listing.images || [])[0];
  const DeliveryIcon = listing.delivery === "shipping" ? Truck : listing.delivery === "local" ? Hand : Repeat;

  return (
    <div
      className="absolute inset-0 select-none rounded-2xl overflow-hidden bg-white border border-slate-200 shadow-[0_18px_40px_-12px_rgba(15,23,42,0.25)]"
      style={{
        transform: `translate(${drag.x}px, ${drag.y}px) rotate(${rotation}deg)`,
        transition: drag.dragging ? "none" : "transform 0.32s cubic-bezier(.2,.8,.2,1)",
        zIndex: isTop ? 30 : 20,
        opacity: dimmed ? 0.92 : 1,
        scale: dimmed ? "0.97" : "1",
        touchAction: "none",
        pointerEvents: isTop ? "auto" : "none",
      }}
    >
      {/* Image hero */}
      <div className="relative h-[58%] bg-slate-100">
        {img ? (
          <img src={imgSrc(img)} alt={listing.title} className="w-full h-full object-cover" draggable={false} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-300 text-xs uppercase tracking-wide">No image</div>
        )}
        {/* Like/Pass stamps */}
        {drag.x > 40 && (
          <div className="absolute top-6 left-6 -rotate-12 border-4 border-emerald-500 text-emerald-600 px-3 py-1 rounded-md font-bold tracking-widest text-lg" style={{ opacity: Math.min(1, drag.x / DRAG_THRESHOLD) }}>
            INTERESTED
          </div>
        )}
        {drag.x < -40 && (
          <div className="absolute top-6 right-6 rotate-12 border-4 border-rose-500 text-rose-600 px-3 py-1 rounded-md font-bold tracking-widest text-lg" style={{ opacity: Math.min(1, -drag.x / DRAG_THRESHOLD) }}>
            PASS
          </div>
        )}
        {/* Delivery */}
        <span className="absolute top-3 left-3 bg-white/95 border border-slate-200 px-2 py-0.5 rounded text-[11px] font-semibold text-slate-700 flex items-center gap-1">
          <DeliveryIcon className="w-3 h-3" />
          {listing.delivery === "shipping" ? "Ships" : listing.delivery === "local" ? "Local" : "Local · Ships"}
        </span>
      </div>

      {/* Body */}
      <div className="p-4 flex flex-col gap-2 h-[42%] overflow-hidden">
        <div>
          <h2 className="font-display font-bold text-lg leading-tight line-clamp-2">{listing.title}</h2>
          <p className="text-xs text-slate-500 flex items-center gap-3 mt-1 flex-wrap">
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {distText || listing.location}</span>
            <span className="vyrti-pill !text-[10px] !py-0.5">{listing.category}</span>
          </p>
        </div>

        {wants.length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1">Wants</p>
            <div className="flex flex-wrap gap-1">
              {wants.map((w) => (
                <span key={`want-${w}`} className="bg-[#E0EDFA] text-[#0654BA] text-[11px] font-semibold px-2 py-0.5 rounded">{w}</span>
              ))}
              {listing.open_to_offers && <span className="bg-[#FFF6DE] text-[#92400E] text-[11px] font-semibold px-2 py-0.5 rounded">+ Open</span>}
            </div>
          </div>
        )}

        <div className="mt-auto pt-2 border-t border-slate-100 flex items-center gap-2">
          {owner.picture ? (
            <img src={imgSrc(owner.picture)} alt={owner.name} className="w-7 h-7 rounded-full object-cover" />
          ) : (
            <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-[11px] font-bold text-slate-700">{owner.name?.[0]?.toUpperCase()}</div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[12px] font-semibold text-slate-800 truncate flex items-center gap-1">
              {owner.name}
              {owner.verified && <BadgeCheck className="w-3.5 h-3.5 text-[#0654BA]" />}
            </p>
            {owner.rating_count > 0 ? (
              <p className="text-[10px] text-emerald-700 font-semibold flex items-center gap-0.5">
                <ThumbsUp className="w-2.5 h-2.5" />{Math.round(owner.positive_pct || 0)}% positive
                {owner.vouch_count > 0 && (
                  <span className="text-[#0654BA] flex items-center gap-0.5 ml-1.5" data-testid="swipe-vouch-chip">
                    <HeartHandshake className="w-2.5 h-2.5" />{owner.vouch_count}
                  </span>
                )}
              </p>
            ) : owner.vouch_count > 0 ? (
              <p className="text-[10px] text-[#0654BA] font-semibold flex items-center gap-0.5" data-testid="swipe-vouch-chip">
                <HeartHandshake className="w-2.5 h-2.5" />{owner.vouch_count} {owner.vouch_count === 1 ? "vouch" : "vouches"}
              </p>
            ) : (
              <p className="text-[10px] text-slate-500">New trader</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SwipeDiscover() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [deck, setDeck] = useState([]);   // array of listings
  const [loading, setLoading] = useState(true);
  const [drag, setDrag] = useState({ x: 0, y: 0, dragging: false });
  const [lastAction, setLastAction] = useState(null); // {listing, direction} for undo
  const dragStartRef = useRef({ x: 0, y: 0 });

  const fetchDeck = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/swipe/queue", { params: { limit: 15 } });
      setDeck(prev => [...prev, ...(data.items || [])]);
    } catch (e) {
      console.warn("Swipe queue fetch failed", e);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    fetchDeck();
  }, [user, fetchDeck, navigate]);

  // Pre-fetch when running low
  useEffect(() => {
    if (deck.length > 0 && deck.length <= 3 && !loading) fetchDeck();
  }, [deck.length, loading, fetchDeck]);

  const top = deck[0];
  const next = deck[1];

  const finishSwipe = async (direction) => {
    if (!top) return;
    setLastAction({ listing: top, direction });
    setDeck(prev => prev.slice(1));
    setDrag({ x: 0, y: 0, dragging: false });
    try {
      const { data } = await api.post("/swipe", { listing_id: top.id, direction });
      if (direction === "like") {
        if (data.match) {
          toast.success("It's a match! 🎉", { description: `Both you and ${top.owner?.name} liked each other.` });
        } else {
          toast.success(`Interest sent to ${top.owner?.name || "the owner"}`);
        }
      }
    } catch (e) {
      toast.error("Could not record swipe");
    }
  };

  const handleLike = () => animateExit("right");
  const handlePass = () => animateExit("left");

  const animateExit = (dir) => {
    if (!top) return;
    setDrag({ x: dir === "right" ? 520 : -520, y: 0, dragging: false });
    setTimeout(() => finishSwipe(dir === "right" ? "like" : "pass"), 280);
  };

  const onPointerDown = (e) => {
    if (!top) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    setDrag(d => ({ ...d, dragging: true }));
  };
  const onPointerMove = (e) => {
    if (!drag.dragging) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    setDrag({ x: dx, y: dy, dragging: true });
  };
  const onPointerUp = (e) => {
    if (!drag.dragging) return;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    if (Math.abs(drag.x) > DRAG_THRESHOLD) {
      animateExit(drag.x > 0 ? "right" : "left");
    } else {
      setDrag({ x: 0, y: 0, dragging: false });
    }
  };

  const rotation = drag.x / 20;

  return (
    <div className="section-pad py-4 md:py-8 min-h-[calc(100vh-3.5rem)] flex flex-col" data-testid="swipe-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => navigate(-1)} className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5" data-testid="swipe-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <h1 className="font-display font-bold text-lg md:text-2xl text-slate-900">Discover</h1>
        <Link to="/swipes/interested" className="tx-link text-[12px] font-semibold flex items-center gap-1" data-testid="swipe-interested-link">
          <Sparkles className="w-3.5 h-3.5" />
          Interested
        </Link>
      </div>

      <p className="text-[12px] text-slate-500 text-center mb-3">Swipe right to flag interest. Owners see who&apos;s into their items.</p>

      {/* Card area */}
      <div
        className="relative mx-auto w-full max-w-sm flex-1 max-h-[640px] mb-4 select-none"
        style={{ minHeight: 520 }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        data-testid="swipe-stack"
      >
        {next && <ListingCardSwipe listing={next} drag={{ x: 0, y: 0, dragging: false }} rotation={0} isTop={false} dimmed />}
        {top && <ListingCardSwipe listing={top} drag={drag} rotation={rotation} isTop />}
        {!top && (
          <div className="absolute inset-0 rounded-2xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center p-6 text-center">
            {loading ? (
              <>
                <div className="w-10 h-10 rounded-full border-4 border-slate-200 border-t-[#0654BA] animate-spin mb-3" />
                <p className="text-sm text-slate-500">Loading more trades…</p>
              </>
            ) : (
              <>
                <Sparkles className="w-8 h-8 text-[#F5AF02] mb-3" />
                <h2 className="font-display font-bold text-lg text-slate-900">You&apos;re all caught up</h2>
                <p className="text-sm text-slate-500 mt-1 max-w-xs">No more new listings to discover right now. Check back soon — new items appear daily.</p>
                <button onClick={fetchDeck} className="mt-4 btn-block btn-ghost !text-sm">
                  <RotateCcw className="w-4 h-4" /> Refresh
                </button>
                <Link to="/browse" className="text-[12px] tx-link mt-2">Or browse the marketplace →</Link>
              </>
            )}
          </div>
        )}
      </div>

      {/* Action buttons */}
      {top && (
        <div className="flex items-center justify-center gap-5 pb-4">
          <button
            onClick={handlePass}
            className="w-14 h-14 rounded-full bg-white border-2 border-slate-200 hover:border-rose-400 text-rose-500 flex items-center justify-center shadow-md hover:shadow-lg active:scale-95 transition-all"
            data-testid="swipe-pass"
            aria-label="Pass"
          >
            <X className="w-7 h-7" strokeWidth={2.5} />
          </button>
          <Link
            to={`/listing/${top.id}`}
            className="px-4 py-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition-colors"
            data-testid="swipe-view-details"
          >
            View details
          </Link>
          <button
            onClick={handleLike}
            className="w-14 h-14 rounded-full bg-[#0654BA] hover:bg-[#0445A0] text-white flex items-center justify-center shadow-md hover:shadow-lg active:scale-95 transition-all"
            data-testid="swipe-like"
            aria-label="Interested"
          >
            <Heart className="w-7 h-7 fill-current" strokeWidth={2.5} />
          </button>
        </div>
      )}
    </div>
  );
}
